// Reports & Analytics Component
import React, { useState, useEffect } from 'react';
import FeatureGuard from '../common/FeatureGuard';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  TrendingDown,
  People,
  AttachMoney,
  CalendarMonth,
  Receipt,
  Inventory,
  Download,
  Refresh,
  Star,
  ContentCut,
} from '@mui/icons-material';
import { StatCard } from '../common';
import { useCurrency } from '../../contexts/CurrencyContext';
import apiService from '../../services/api';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [timeFrame, setTimeFrame] = useState('thisMonth');
  const [dateRange, setDateRange] = useState('month');
  const [tabValue, setTabValue] = useState(0);
  const [reports, setReports] = useState({
    overview: {},
    services: [],
    products: [],
    dailyBreakdown: [],
    topClients: []
  });
  
  // Use currency context for proper currency formatting
  const { formatPrice: formatCurrency } = useCurrency();

  const dateRangeOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiService.get(`/reports?range=${dateRange}`);
        
        if (response.success) {
          setReports(response.data);
        } else {
          setError('Failed to fetch reports');
        }
      } catch (err) {
        setError('Error fetching reports: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [dateRange]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/reports?range=${dateRange}`);
      
      if (response.success) {
        setReports(response.data);
      } else {
        setError('Failed to fetch reports');
      }
    } catch (err) {
      setError('Error fetching reports: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getGrowthIcon = (value) => {
    return value >= 0 ? <TrendingUp color="success" /> : <TrendingDown color="error" />;
  };

  const getGrowthColor = (value) => {
    return value >= 0 ? 'success.main' : 'error.main';
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n.charAt(0)).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading reports...</Typography>
      </Box>
    );
  }

  return (
    <FeatureGuard feature="Reports & Analytics">
      <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Reports & Analytics
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              {dateRangeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchReports}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => console.log('Export reports')}
          >
            Export
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Overview Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(reports.overview.totalRevenue)}
            icon={<AttachMoney />}
            color="primary"
            growth={reports.overview.revenueGrowth}
            growthLabel={formatPercentage(reports.overview.revenueGrowth)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Clients"
            value={reports.overview.totalClients.toLocaleString()}
            icon={<People />}
            color="info"
            growth={reports.overview.clientGrowth}
            growthLabel={formatPercentage(reports.overview.clientGrowth)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Appointments"
            value={reports.overview.totalAppointments.toLocaleString()}
            icon={<CalendarMonth />}
            color="warning"
            growth={reports.overview.appointmentGrowth}
            growthLabel={formatPercentage(reports.overview.appointmentGrowth)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Ticket"
            value={formatCurrency(reports.overview.averageTicket)}
            icon={<Receipt />}
            color="success"
            growth={0}
            growthLabel="Per visit"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<Assessment />} label="Revenue" />
          <Tab icon={<People />} label="Clients" />
          <Tab icon={<ContentCut />} label="Services" />
          <Tab icon={<Inventory />} label="Products" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {/* Revenue Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue Trend
                </Typography>
                <Box height={300} display="flex" alignItems="center" justifyContent="center">
                  <Typography color="text.secondary">
                    Chart visualization would go here
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue by Service
                </Typography>
                <List>
                  {reports.revenue.services.slice(0, 5).map((service, index) => (
                    <ListItem key={index}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <ContentCut />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={service.name}
                        secondary={formatCurrency(service.revenue)}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Daily Revenue Breakdown
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Services</TableCell>
                        <TableCell>Products</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Growth</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.revenue.daily.slice(0, 7).map((day, index) => (
                        <TableRow key={index}>
                          <TableCell>{day.date}</TableCell>
                          <TableCell>{formatCurrency(day.services)}</TableCell>
                          <TableCell>{formatCurrency(day.products)}</TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">
                              {formatCurrency(day.total)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {getGrowthIcon(day.growth)}
                              <Typography 
                                color={getGrowthColor(day.growth)}
                                sx={{ ml: 0.5 }}
                              >
                                {formatPercentage(day.growth)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Clients Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Clients by Revenue
                </Typography>
                <List>
                  {reports.clients.top.slice(0, 10).map((client, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'secondary.main' }}>
                            {getInitials(client.name)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={client.name}
                          secondary={`${client.visits} visits • ${client.lastVisit}`}
                        />
                        <Box textAlign="right">
                          <Typography variant="subtitle2" fontWeight="bold">
                            {formatCurrency(client.totalSpent)}
                          </Typography>
                          <Chip
                            size="small"
                            label={client.tier}
                            color={client.tier === 'VIP' ? 'primary' : 'default'}
                          />
                        </Box>
                      </ListItem>
                      {index < reports.clients.top.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  New Clients This {dateRange.charAt(0).toUpperCase() + dateRange.slice(1)}
                </Typography>
                <List>
                  {reports.clients.new.slice(0, 10).map((client, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'success.main' }}>
                            {getInitials(client.name)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={client.name}
                          secondary={`Joined ${client.joinDate} • ${client.source}`}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(client.totalSpent)}
                        </Typography>
                      </ListItem>
                      {index < reports.clients.new.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Client Analytics
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="primary.main" fontWeight="bold">
                        {reports.clients.demographics.returningRate || 0}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Returning Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {reports.clients.demographics.averageVisits || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg Visits per Client
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="warning.main" fontWeight="bold">
                        {reports.clients.demographics.averageSpending || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg Spending per Visit
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="info.main" fontWeight="bold">
                        {reports.clients.demographics.daysBetweenVisits || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Days Between Visits
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Services Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Most Popular Services
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell>Bookings</TableCell>
                        <TableCell>Revenue</TableCell>
                        <TableCell>Avg Duration</TableCell>
                        <TableCell>Rating</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.services.popular.map((service, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                <ContentCut />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {service.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {service.category}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">
                              {service.bookings}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">
                              {formatCurrency(service.revenue)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {service.avgDuration} min
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Star sx={{ color: 'gold', mr: 0.5, fontSize: 16 }} />
                              <Typography variant="body2">
                                {service.rating.toFixed(1)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Performance
                </Typography>
                {reports.services.performance.map((service, index) => (
                  <Box key={index} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">{service.name}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {service.utilization}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={service.utilization}
                      color={service.utilization > 80 ? 'success' : service.utilization > 60 ? 'warning' : 'error'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {service.bookings} bookings this {dateRange}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Products Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Product Sales Performance
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Brand</TableCell>
                        <TableCell>Sold</TableCell>
                        <TableCell>Revenue</TableCell>
                        <TableCell>Stock</TableCell>
                        <TableCell>Profit Margin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.revenue.products.map((product, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                                <Inventory />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {product.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {product.category}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{product.brand}</TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">
                              {product.sold} units
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">
                              {formatCurrency(product.revenue)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`${product.stock} left`}
                              color={product.stock < 10 ? 'error' : product.stock < 25 ? 'warning' : 'success'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              color={product.margin > 50 ? 'success.main' : product.margin > 30 ? 'warning.main' : 'error.main'}
                              fontWeight="bold"
                            >
                              {product.margin.toFixed(1)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      </Box>
    </FeatureGuard>
  );
};

export default Reports;
